import { useRef, useMemo, useCallback } from "react";
import {
  FormFieldAndIcons,
  ImageAttribute,
  ImageAttributeProps,
  DocumentAttribute,
  DocumentAttributeProps,
  MultiSelectField,
  ProductReferenceField,
  UserReferenceField,
} from "../../../../../components/AttributeGroupFormGenerator/AttributeGroupFormGenerator";
import { SingleSelectListBackedField } from "../../../../../components/AttributeGroupFormGenerator/ListBackedSelectField";
import { useAuthContext } from "../../../../../components/Auth";
import { shouldUseLazyListChoices } from "../../../../../util/attributeChoices";
import { LinkAttributeField } from "../../../../../components/LinkAttributeField/LinkAttributeField";
import { DatePicker } from "../../../../../components/DatePicker/DatePicker";
import { MethodsOfUseForm } from "../../../../../types/types";
import {
  AttributeSchema,
  PIMProduct,
  ReferenceEntityConstraintSchema,
} from "../../../../../types/types.PIM";
import { useTranslation } from "react-i18next";
import { TextField } from "../../../../../components/TextFields/TextFields";
import { stringArrayToOptionArray } from "../../../../admin/SellerAdmin/PIM/SellerAdminTemplates/AddItemToTemplateCollectionForm";
import { Controller } from "react-hook-form";
import { CreatableSelectBox } from "../../../../../components/CreateableSelectBox/CreatableSelectBox";
import { ToggleSwitchV2 } from "../../../../../components/ToggleSwitch/ToggleSwitch";
import { TextArea } from "../../../../../components/TextArea/TextArea";
import { RichEditor } from "../../../../../components/RichEditor/RichEditor";
import styled from "styled-components";
import { LinkAttributeValueSchema } from "../../../../../util/zod.util";
import { strings } from "../../../../../util/strings";
import { MultiSelectRefType } from "../../../../admin/SellerAdmin/PIM/components/ProductCollectionSchemaTable/type";
import { formatRequiredLabel } from "./utils";
import { sortOptionsByLabel } from "../../../../../util/options";
import { ObjectReferenceField } from "../../../../../components/ObjectReference/ObjectReferenceField";
import { useStoreState } from "../../../../../util/util";
import { FormProvider } from "react-hook-form";
import { AddressAttributeForm } from "../../../../admin/SellerAdmin/PIM/SellerAdminPIMAttributes/AddressAttributes/AddressAttributeForm";
import {
  AddressValue,
  prepareAddressForForm,
} from "../../../../admin/SellerAdmin/PIM/SellerAdminPIMAttributes/AddressAttributes/address.utils";

const RichEditorLocalContainer = styled.div`
  .ql-container {
    height: fit-content;
  }
`;

const GroupAttributeFieldComponent = ({
  attr,
  methodsOfUseForm,
  isDisabled,
  onCreateImageAttribute,
  onCreateDocumentAttribute,
  attributeId,
  hasReferenceEntityMapping,
  dependentAttributes,
  onFieldChange,
  clearableAttributes,
  multiSelectRefs,
  product,
}: {
  attr: AttributeSchema;
  methodsOfUseForm: MethodsOfUseForm;
  isDisabled?: boolean;
  onCreateImageAttribute?: ImageAttributeProps["onCreateImageAttribute"];
  onCreateDocumentAttribute?: DocumentAttributeProps["onCreateDocumentAttribute"];
  attributeId: string;
  hasReferenceEntityMapping?: boolean;
  dependentAttributes?: ReferenceEntityConstraintSchema["constraints"];
  onFieldChange?: (attributeId: string, clearSelection?: boolean) => void;
  clearableAttributes?: Set<string>;
  multiSelectRefs: React.MutableRefObject<Record<string, MultiSelectRefType>>;
  product?: PIMProduct;
}) => {
  const { t } = useTranslation();
  const { tenant_id } = useStoreState();
  const { hasPermission } = useAuthContext();
  const { register, formState, errors, control, setValue, watch } =
    methodsOfUseForm;

  const datePickerRef = useRef<{ updateDate: (date: string) => void }>(null);

  // Memoize the attribute object to prevent unnecessary re-renders in MultiSelectField
  const attrValuesString = JSON.stringify(attr.values);
  const attrChoicesString = JSON.stringify(attr.choices);
  const memoizedAttr = useMemo(
    () => ({
      ...attr,
      id: attributeId,
      is_editable: true,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      attr.id,
      attr.name,
      attr.input_type,
      attr.is_required,
      attr.is_editable,
      attributeId,
      attr.list_id,
      attrValuesString,
      attrChoicesString,
    ]
  );

  const isClearable = useCallback(
    (col_id: string) => {
      return hasReferenceEntityMapping
        ? clearableAttributes?.has(col_id) ||
            (clearableAttributes || new Set()).size === 0
        : true;
    },
    [clearableAttributes, hasReferenceEntityMapping]
  );

  const isAutofilled = useCallback(
    (col_id: string) => {
      const constraint = dependentAttributes?.find(
        (c) => c.attribute_id === col_id
      );
      return constraint?.action === "auto_fill";
    },
    [dependentAttributes]
  );

  return (() => {
    switch (attr.input_type) {
      case "date":
        return (
          <FormFieldAndIcons
            key={attributeId}
            description={attr.description ? t([attr.description]) : null}
            onClickNotApplicable={() => {}}
            attribute={attr}
            pimProductType={"standard"}
            isDisabled={isDisabled}
            showApplicableButton={false}
          >
            <DatePicker
              isOutsideRange={() => false}
              label={formatRequiredLabel(attr, t)}
              name={attributeId}
              defaultValue={
                attr.values?.length
                  ? (attr.values[0].value as string)
                  : undefined
              }
              required={attr.is_required}
              methodsOfUseForm={methodsOfUseForm}
              disabled={isDisabled}
              rules={{
                required: attr.is_required,
                message: t("This is a required field"),
              }}
              ref={datePickerRef}
            />
          </FormFieldAndIcons>
        );
      case "form_field": {
        return (
          <FormFieldAndIcons
            key={attr.id}
            description={attr.description ? t([attr.description]) : null}
            onClickNotApplicable={() => {}}
            attribute={attr}
            pimProductType={"standard"}
            isDisabled={isDisabled}
            showApplicableButton={false}
          >
            <TextField
              name={attributeId}
              label={formatRequiredLabel(attr, t)}
              theref={register({
                required: attr.is_required,
              })}
              defaultValue={
                attr.values?.length
                  ? (attr.values[0].value as string)
                  : undefined
              }
              formState={formState}
              errors={errors}
              type="text"
              readOnly={attr.object_type === "product_id" || isDisabled}
            />
          </FormFieldAndIcons>
        );
      }
      case "single_select": {
        const listBackedAttribute = { ...attr, id: attributeId };
        const refEntityConstraint = dependentAttributes?.find(
          (c) => c.attribute_id === attributeId
        );
        const hasRefMappingForAttr =
          !!hasReferenceEntityMapping && refEntityConstraint !== undefined;

        if (
          tenant_id &&
          shouldUseLazyListChoices(listBackedAttribute, {
            hasReferenceEntityMapping: hasRefMappingForAttr,
            validValues: refEntityConstraint?.valid_values,
          })
        ) {
          return (
            <FormFieldAndIcons
              key={attr.id}
              description={attr.description ? t([attr.description]) : null}
              onClickNotApplicable={() => {}}
              attribute={attr}
              pimProductType={"standard"}
              isDisabled={isDisabled}
              showApplicableButton={false}
            >
              <SingleSelectListBackedField
                tenantId={tenant_id}
                attribute={listBackedAttribute}
                control={control}
                errors={errors}
                formState={formState}
                isDisabled={!!isDisabled}
                isClearable={isClearable(attributeId)}
                isAutofilled={isAutofilled(attributeId)}
                canCreateOptions={hasPermission("modify_lists")}
              />
            </FormFieldAndIcons>
          );
        }

        const choices = attr?.choices?.map(stringArrayToOptionArray) ?? [];
        // Use filtered options from reference entity constraints if available
        const dependentChoices = refEntityConstraint?.valid_values?.map(
          (v) => ({ label: v.display, value: v.display })
        );
        const singleSelectOptions =
          hasRefMappingForAttr && dependentChoices
            ? sortOptionsByLabel(dependentChoices)
            : choices;

        return (
          <FormFieldAndIcons
            key={attr.id}
            description={attr.description ? t([attr.description]) : null}
            onClickNotApplicable={() => {}}
            attribute={attr}
            pimProductType={"standard"}
            isDisabled={isDisabled}
            showApplicableButton={false}
          >
            <Controller
              control={control}
              name={attributeId}
              rules={{
                required: attr.is_required
                  ? strings(t).thisIsARequiredField
                  : false,
              }}
              defaultValue={
                attr?.values?.length
                  ? {
                      label: attr.values[0].enum_variant?.variant,
                      value: attr.values[0].enum_variant?.variant,
                    }
                  : null
              }
              render={({ onChange, value }) => (
                <CreatableSelectBox
                  placeholder={formatRequiredLabel(attr, t)}
                  options={singleSelectOptions}
                  isClearable={isClearable(attributeId)}
                  isAutofilled={isAutofilled(attributeId)}
                  name={attributeId}
                  value={value}
                  onChange={(
                    option: { label: string; value: string } | null
                  ) => {
                    onChange(option, { shouldValidate: true });
                    !!onFieldChange &&
                      onFieldChange?.(attributeId, option === null);
                  }}
                  errors={errors}
                  formState={formState}
                  disabled={isDisabled}
                />
              )}
            />
          </FormFieldAndIcons>
        );
      }
      case "toggle":
      case "checkbox":
        return (
          <FormFieldAndIcons
            key={attr.id}
            description={attr.description ? t([attr.description]) : null}
            onClickNotApplicable={() => {}}
            attribute={attr}
            pimProductType={"standard"}
            isDisabled={isDisabled}
            showApplicableButton={false}
          >
            <div>
              <ToggleSwitchV2
                label={formatRequiredLabel(attr, t)}
                name={attributeId}
                ref={register()}
                defaultChecked={
                  attr.values && (attr.values[0]?.value as boolean)
                }
                disabled={isDisabled}
              />
            </div>
          </FormFieldAndIcons>
        );
      case "numeric": {
        return (
          <FormFieldAndIcons
            key={attr.id}
            description={attr.description ? t([attr.description]) : null}
            onClickNotApplicable={() => {}}
            attribute={attr}
            pimProductType={"standard"}
            isDisabled={isDisabled}
            showApplicableButton={false}
          >
            <TextField
              name={attributeId}
              label={`${formatRequiredLabel(attr, t)} (number)`}
              theref={register({
                required: attr.is_required,
              })}
              defaultValue={
                attr.values?.length
                  ? (attr.values[0].value as string)
                  : undefined
              }
              formState={formState}
              errors={errors}
              type="number"
              readOnly={isDisabled}
            />
          </FormFieldAndIcons>
        );
      }
      case "multiline_entry":
        return (
          <FormFieldAndIcons
            key={attr.id}
            description={attr.description ? t([attr.description]) : null}
            onClickNotApplicable={() => {}}
            attribute={attr}
            pimProductType={"standard"}
            isDisabled={isDisabled}
            showApplicableButton={false}
          >
            {attr.name === "Meta Keywords" ||
            attr.name === "Meta Description" ? (
              <TextArea
                name={attributeId}
                label={formatRequiredLabel(attr, t)}
                theref={register({ required: attr.is_required })}
                formState={formState}
                required={attr.is_required}
                defaultValue={
                  attr.values?.length
                    ? (attr.values[0].value as string)
                    : undefined
                }
                readOnly={isDisabled}
                errors={errors}
              />
            ) : (
              <RichEditorLocalContainer>
                <RichEditor
                  value={
                    attr.values?.length
                      ? (attr.values[0].value as string)
                      : undefined
                  }
                  name={attributeId}
                  label={formatRequiredLabel(attr, t)}
                  useFormMethods={{
                    register,
                    formState,
                    setValue,
                    errors,
                    watch,
                  }}
                  readOnly={isDisabled}
                  validationRules={{
                    required: attr.is_required
                      ? strings(t).thisIsARequiredField
                      : false,
                  }}
                />
              </RichEditorLocalContainer>
            )}
          </FormFieldAndIcons>
        );
      case "multi_select": {
        const refEntityConstraint = dependentAttributes?.find(
          (c) => c.attribute_id === attributeId
        );
        const hasRefMappingForAttr =
          !!hasReferenceEntityMapping && refEntityConstraint !== undefined;
        const useLazyListChoices =
          !!tenant_id &&
          shouldUseLazyListChoices(memoizedAttr, {
            hasReferenceEntityMapping: hasRefMappingForAttr,
            validValues: refEntityConstraint?.valid_values,
          });
        const multiSelectDependentChoices = hasRefMappingForAttr
          ? sortOptionsByLabel(
              refEntityConstraint?.valid_values?.map((v) => ({
                label: v.display,
                value: v.display,
              })) ?? []
            )
          : undefined;

        return (
          <FormFieldAndIcons
            key={attr.id}
            description={attr.description ? t([attr.description]) : null}
            onClickNotApplicable={() => {}}
            attribute={attr}
            pimProductType={"standard"}
            isDisabled={isDisabled}
            showApplicableButton={false}
          >
            <MultiSelectField
              attribute={memoizedAttr}
              type={"group"}
              methodsOfUseForm={methodsOfUseForm}
              multiSelectRefs={multiSelectRefs}
              isDisabled={isDisabled || isAutofilled(attributeId)}
              isNotApplicable={false}
              dependent_choices={
                useLazyListChoices ? undefined : multiSelectDependentChoices
              }
              updateDependentAttributes={(params) =>
                onFieldChange?.(attributeId, params.clearSelection)
              }
            />
          </FormFieldAndIcons>
        );
      }
      case "link":
        const maybeDefaultValue = (() => {
          if (attr?.values?.[0]?.value && !attr.is_not_applicable) {
            const parsed = LinkAttributeValueSchema.safeParse(
              attr.values[0]?.value
            );
            if (parsed.success) {
              return parsed.data as { url: string; display_text: string };
            }
          }
          return { url: "", display_text: "" };
        })();

        return (
          <FormFieldAndIcons
            key={attributeId}
            description={attr.description ? t([attr.description]) : null}
            onClickNotApplicable={() => {}}
            attribute={attr}
            pimProductType={"standard"}
            isDisabled={isDisabled}
            showApplicableButton={false}
          >
            <LinkAttributeField
              name={attributeId}
              label={formatRequiredLabel(attr, t)}
              control={control}
              register={register}
              formState={formState}
              errors={errors}
              defaultValue={maybeDefaultValue}
              isRequired={attr.is_required}
              isDisabled={isDisabled}
              layout="horizontal"
            />
          </FormFieldAndIcons>
        );
      case "user":
        // Get filtered options for user field from reference entity constraints
        const userDependentOptions = hasReferenceEntityMapping
          ? dependentAttributes
              ?.find((c) => c.attribute_id === attributeId)
              ?.valid_values?.map((v) => ({
                label: v.display,
                value: v.display,
              }))
          : undefined;

        return (
          <FormFieldAndIcons
            key={attr.id}
            description={attr.description ? t([attr.description]) : null}
            onClickNotApplicable={() => {}}
            attribute={attr}
            pimProductType={"standard"}
            isDisabled={isDisabled}
            showApplicableButton={false}
          >
            <UserReferenceField
              attribute={memoizedAttr}
              methodsOfUseForm={methodsOfUseForm}
              isNotApplicable={false}
              isTemplate={false}
              isDisabled={isAutofilled(attributeId)}
              dependent_attributes_options={userDependentOptions}
              updateDependentAttributes={(params) =>
                onFieldChange?.(attributeId, params.clearSelection)
              }
            />
          </FormFieldAndIcons>
        );
      case "product_reference":
        return (
          <FormFieldAndIcons
            key={attr.id}
            description={attr.description ? t([attr.description]) : null}
            onClickNotApplicable={() => {}}
            attribute={attr}
            pimProductType={"standard"}
            isDisabled={isDisabled}
            showApplicableButton={false}
          >
            <ProductReferenceField
              attribute={memoizedAttr}
              methodsOfUseForm={methodsOfUseForm}
              isDisabled={isDisabled || isAutofilled(attributeId)}
              isNotApplicable={false}
            />
          </FormFieldAndIcons>
        );
      case "object_reference":
        return (
          <FormFieldAndIcons
            key={attr.id}
            description={attr.description ? t([attr.description]) : null}
            onClickNotApplicable={() => {}}
            attribute={attr}
            pimProductType={"standard"}
            isDisabled={isDisabled}
            showApplicableButton={false}
          >
            <ObjectReferenceField
              attribute={
                memoizedAttr as Extract<
                  AttributeSchema,
                  { input_type: "object_reference" }
                >
              }
              methodsOfUseForm={methodsOfUseForm}
              isDisabled={isDisabled || isAutofilled(attributeId)}
              isNotApplicable={false}
            />
          </FormFieldAndIcons>
        );
      case "image":
        return (
          <FormFieldAndIcons
            key={attr.id}
            description={attr.description ? t([attr.description]) : null}
            onClickNotApplicable={() => {}}
            attribute={attr}
            pimProductType={"standard"}
            isDisabled={isDisabled}
            showApplicableButton={false}
          >
            <ImageAttribute
              attribute={attr}
              methodsOfUseForm={methodsOfUseForm}
              onCreateImageAttribute={onCreateImageAttribute}
              isDisabled={isDisabled}
            />
          </FormFieldAndIcons>
        );
      case "document":
        return (
          <FormFieldAndIcons
            key={attr.id}
            description={attr.description ? t([attr.description]) : null}
            onClickNotApplicable={() => {}}
            attribute={attr}
            pimProductType={"standard"}
            isDisabled={isDisabled}
            showApplicableButton={false}
          >
            <DocumentAttribute
              attribute={attr}
              methodsOfUseForm={methodsOfUseForm}
              isDisabled={isDisabled}
              isNotApplicable={attr.is_not_applicable ?? false}
              product={product}
              onCreateDocumentAttribute={onCreateDocumentAttribute}
              uploadNewDocumentsOnly
              hideAccessibility
            />
          </FormFieldAndIcons>
        );
      case "address": {
        const addressAttr = attr as Extract<
          AttributeSchema,
          { input_type: "address" }
        >;
        const addressSavedValue =
          attr.values?.length && !attr.is_not_applicable
            ? prepareAddressForForm(attr.values[0].value as AddressValue)
            : undefined;

        return (
          <FormFieldAndIcons
            key={attributeId}
            description={attr.description ? t([attr.description]) : null}
            onClickNotApplicable={() => {}}
            attribute={attr}
            pimProductType={"standard"}
            isDisabled={isDisabled}
            showApplicableButton={false}
          >
            <FormProvider {...methodsOfUseForm}>
              <AddressAttributeForm
                key={`${attributeId}-${attr.is_not_applicable}`}
                fieldPrefix={attributeId}
                mode={addressAttr.address_mode ?? "standard"}
                attributeName={formatRequiredLabel(attr, t)}
                savedValue={addressSavedValue}
                isNotApplicable={attr.is_not_applicable ?? false}
              />
            </FormProvider>
          </FormFieldAndIcons>
        );
      }
      default:
        return <></>; //assertUnreachable(attr);
    }
  })();
};

// Export component without memo for now to debug multi-select issue
export const GroupAttributeField = GroupAttributeFieldComponent;
